import { BehaviorSubject, forkJoin, Observable, of, Subject, filter, first, map, switchMap, takeUntil } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { v4 } from 'uuid';

import { CurrentProductService, ProductListComponentService } from '@spartacus/storefront';
import { AuthService, RoutingService, User } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { OrderEntry } from '@spartacus/cart/base/root';

import { ConfigurationService } from './configuration.service';
import { PrudsysRecommendation } from '../models';
import { PrudsysServiceEnum } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class PrudsysService implements OnDestroy {
  private sidStorageKey = 'prudsys_sid';
  private sid: string;

  private services: { [id: string]: BehaviorSubject<PrudsysRecommendation> } = {};
  private servicesStatus: { [id: string]: { loading: boolean; loadedAt: number } } = {};
  protected readonly destroy$: Subject<void> = new Subject();

  constructor(
    private configService: ConfigurationService,
    private http: HttpClient,
    private authService: AuthService,
    private userAccountService: UserAccountFacade,
    private routingService: RoutingService,
    private currentProductService: CurrentProductService,
    private cartService: ActiveCartService,
    private productListComponentService: ProductListComponentService
  ) {}

  getRecommendations(serviceId: string): Observable<PrudsysRecommendation> {
    // TODO-EL: use share operator to avoid manually compute cache time.
    // Example:
    // https://stackoverflow.com/questions/77397049/rxjs-sharereplay-with-cache-invalidation-after-time
    // return wsCall().pipe(
    //   share({
    //     connector: () => new ReplaySubject(),
    //     resetOnRefCountZero: () => timer(5_000),
    //     resetOnComplete: false,
    //     resetOnError: false
    //   })
    // )

    const serviceStatus = this.servicesStatus[serviceId];

    if (!serviceStatus) {
      this.servicesStatus[serviceId] = { loading: true, loadedAt: undefined };
      this.services[serviceId] = new BehaviorSubject(undefined);
      this.loadRecommendations(serviceId);
    } else if (!serviceStatus.loading && Date.now() - serviceStatus.loadedAt > 2000) {
      this.servicesStatus[serviceId].loading = true;
      this.loadRecommendations(serviceId);
    }

    return this.services[serviceId].pipe(filter((recommendation) => recommendation !== undefined));
  }

  sendProductViewEvent(productId: string): Observable<void> {
    const params = { pid: productId };
    return this.sendEvent('productview', params);
  }

  sendCategoryViewEvent(categoryId: string): Observable<void> {
    const params = { cid: categoryId };
    return this.sendEvent('categoryview', params);
  }

  sendBasketEvent(productIds: string[], quantities: number[]): Observable<void> {
    const params = { pids: productIds.join(','), quantities: quantities.join(',') };
    return this.sendEvent('basket', params);
  }

  sendOrderEvent(entries: OrderEntry[]): Observable<void> {
    const pids: string[] = [];
    const quantities: number[] = [];
    const totals: string[] = [];
    entries.forEach((entry) => {
      pids.push(entry.product.code);
      quantities.push(entry.quantity);
      totals.push(entry.totalPrice.value.toFixed(2));
    });
    const params = { pids: pids.join(','), quantities: quantities.join(','), totals: totals.join(',') };
    return this.sendEvent('order', params);
  }

  sendSearchTermEvent(term: string): Observable<void> {
    const params = { searchterm: term };
    return this.sendEvent('search', params);
  }

  isEnabled(): boolean {
    const config = this.configService.config;
    return !!config.prudsys?.host && !!config.prudsys?.reid;
  }

  private sendEvent(eventId: string, params: { [param: string]: string | string[] }): Observable<void> {
    const config = this.configService.config;

    const userId$: Observable<User> = this.authService.isUserLoggedIn().pipe(
      switchMap((isLoggedIn) => (isLoggedIn ? this.userAccountService.get() : of(undefined))),
      first()
    );

    return userId$.pipe(
      switchMap((user) => {
        const sid = this.getCurrentSessionId();
        const url = `${config.prudsys?.host}/rde_server/res/${config.prudsys?.reid}/plugins/exec/prudsys/prudsys/event/${eventId}`;

        params['sid'] = sid;
        params['tracking'] = String(this.isTrackingAllowed());

        if (user !== undefined) {
          params['userid'] = user.uid;
        }

        return this.http.get<void>(url, { params });
      })
    );
  }

  private loadRecommendations(serviceId: string): void {
    const config = this.configService.config;

    const userId$: Observable<User> = this.authService.isUserLoggedIn().pipe(
      switchMap((isLoggedIn) => (isLoggedIn ? this.userAccountService.get() : of(undefined))),
      first()
    );

    forkJoin([userId$, this.getRecommendationsServiceParams(serviceId)]).pipe(
      switchMap(([user, params]) => {
        const url = `${config.prudsys?.host}/rde_server/res/${config.prudsys?.reid}/plugins/exec/prudsys/prudsys/core/recommendation/${serviceId}`;

        params['sid'] = this.getCurrentSessionId();
        params['tracking'] = String(this.isTrackingAllowed());
        if (user !== undefined) {
          params['userid'] = user.uid;
        }

        return this.http.get<PrudsysRecommendation>(url, { params });
      }),
      takeUntil(this.destroy$)
    ).subscribe((recommendation) => {
      this.services[serviceId].next(recommendation);
      this.servicesStatus[serviceId].loading = false;
      this.servicesStatus[serviceId].loadedAt = Date.now();
    });
  }

  private getRecommendationsServiceParams(serviceId: string): Observable<{ [param: string]: string | string[] }> {
    switch (serviceId) {
      case PrudsysServiceEnum.CATEGORY_ENTRY.valueOf():
      case PrudsysServiceEnum.CATEGORY_OVERVIEW.valueOf():
        return this.routingService.getRouterState().pipe(
          map((state) => ({ cid: state.state.params['categoryCode'] })),
          first()
        );
      case PrudsysServiceEnum.PRODUCT_DETAIL.valueOf():
        return this.currentProductService.getProduct().pipe(
          map((product) => ({ pid: product?.code, cid: product?.categories[0]?.code })),
          first()
        );
      case PrudsysServiceEnum.BASKET.valueOf():
        return this.cartService.getEntries().pipe(
          map((entries) => ({ currentBasket: entries.map((entry) => entry.product.code).join(',') })),
          first()
        );
      case PrudsysServiceEnum.SEARCH.valueOf():
        return this.productListComponentService.model$.pipe(
          map((searchPage) => ({ searchterm: searchPage.freeTextSearch })),
          first()
        );
      default:
        return of({});
    }
  }

  private getCurrentSessionId(): string {
    if (this.sid === undefined) {
      this.sid = sessionStorage.getItem(this.sidStorageKey);
      if (this.sid === null) {
        this.sid = v4();
        sessionStorage.setItem(this.sidStorageKey, this.sid);
      }
    }
    return this.sid;
  }

  private isTrackingAllowed(): boolean {
    return true; // TODO get tracking from cookie
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
